import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Target, DollarSign, Loader2, Save, Sparkles, ChevronDown, ChevronUp,
  Users, PhoneCall, FileText, Briefcase, FileCheck, CircleCheck, BadgeCheck,
} from 'lucide-react';
import { backendUrl } from './gameplanUtils';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import AlertSettingsSection from './jarvis/AlertSettingsSection';
import StageLimitsSection from './jarvis/StageLimitsSection';

/**
 * GoalsSettingsDialog — One Broker One Pod model (Feb 2026)
 * ─────────────────────────────────────────────────────────
 * Single input: Monthly Settlement Target ($). The system derives the full
 * 7-stage activity funnel (Leads → Discovery → Portals → Proposals →
 * Lodgements → Approvals → Settlements) automatically.
 *
 * Advanced section: override avg loan size + the 6 conversion percentages
 * if your funnel differs from the Pod plan defaults.
 */

const PRESETS = [10_000_000, 20_000_000, 30_000_000, 40_000_000, 50_000_000];

const DEFAULT_CONV = {
  avg_loan: 550000,
  lead_to_discovery: 55,
  discovery_to_portal: 70,
  portal_to_proposal: 90,
  proposal_to_lodgement: 85,
  lodgement_to_approval: 80,
  approval_to_settlement: 90,
};

const STAGE_META = {
  leads: { icon: Users, color: 'text-blue-600' },
  discovery: { icon: PhoneCall, color: 'text-indigo-600' },
  portals: { icon: FileText, color: 'text-violet-600' },
  proposals: { icon: Briefcase, color: 'text-purple-600' },
  lodgements: { icon: FileCheck, color: 'text-amber-600' },
  approvals: { icon: BadgeCheck, color: 'text-orange-600' },
  settlements: { icon: CircleCheck, color: 'text-emerald-600' },
  settlement_volume: { icon: DollarSign, color: 'text-emerald-700' },
};

const fmtAUD = (n, compact = true) => {
  const num = Number(n) || 0;
  if (!compact) return num.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(0)}k`;
  return `$${num.toFixed(0)}`;
};

const fmtNum = (n) => Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 });

const GoalsSettingsDialog = ({ open, onOpenChange, month, authToken, onSaved }) => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [volume, setVolume] = useState('');
  const [lodgeVolume, setLodgeVolume] = useState('');
  const [conv, setConv] = useState(DEFAULT_CONV);
  const [funnelPreview, setFunnelPreview] = useState(null);

  // Load existing targets when dialog opens
  useEffect(() => {
    if (!open || !authToken || !month) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/gameplan/targets/${month}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const t = await res.json();
        if (!active) return;
        const v = Number(t.monthly_settlement_volume_target)
          || Number(t.monthly_settled_target)
          || Number(t.monthly_target_amount)
          || '';
        setVolume(v);
        setLodgeVolume(Number(t.monthly_submitted_target) || '');
        setConv({
          avg_loan: Number(t.funnel_avg_loan_size) || DEFAULT_CONV.avg_loan,
          lead_to_discovery: Math.round((Number(t.funnel_lead_to_discovery) || DEFAULT_CONV.lead_to_discovery / 100) * 100),
          discovery_to_portal: Math.round((Number(t.funnel_discovery_to_portal) || DEFAULT_CONV.discovery_to_portal / 100) * 100),
          portal_to_proposal: Math.round((Number(t.funnel_portal_to_proposal) || DEFAULT_CONV.portal_to_proposal / 100) * 100),
          proposal_to_lodgement: Math.round((Number(t.funnel_proposal_to_lodgement) || DEFAULT_CONV.proposal_to_lodgement / 100) * 100),
          lodgement_to_approval: Math.round((Number(t.funnel_lodgement_to_approval) || DEFAULT_CONV.lodgement_to_approval / 100) * 100),
          approval_to_settlement: Math.round((Number(t.funnel_approval_to_settlement) || DEFAULT_CONV.approval_to_settlement / 100) * 100),
        });
      } catch (e) {
        // silent — broker can still enter fresh values
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, month, authToken]);

  // Live local preview math (mirrors backend so user sees results without an extra round-trip)
  const preview = useMemo(() => {
    const vol = Number(volume) || 0;
    const avg = Number(conv.avg_loan) || 550000;
    if (vol <= 0 || avg <= 0) return null;
    const clamp = (v, def) => {
      const n = Number(v) / 100;
      return n > 0 && n <= 1 ? n : def;
    };
    const a2s = clamp(conv.approval_to_settlement, 0.9);
    const l2a = clamp(conv.lodgement_to_approval, 0.8);
    const p2l = clamp(conv.proposal_to_lodgement, 0.85);
    const po2p = clamp(conv.portal_to_proposal, 0.9);
    const d2po = clamp(conv.discovery_to_portal, 0.7);
    const l2d = clamp(conv.lead_to_discovery, 0.55);
    const settlements = Math.ceil(vol / avg);
    const approvals = Math.ceil(settlements / a2s);
    const lodgements = Math.ceil(approvals / l2a);
    const proposals = Math.ceil(lodgements / p2l);
    const portals = Math.ceil(proposals / po2p);
    const discovery = Math.ceil(portals / d2po);
    const leads = Math.ceil(discovery / l2d);
    // Working days in selected month
    const [y, m] = (month || '2026-01').split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    let wd = 0;
    for (let d = 1; d <= last; d += 1) {
      const dow = new Date(y, m - 1, d).getDay();
      if (dow !== 0 && dow !== 6) wd += 1;
    }
    const weeks = wd > 0 ? wd / 5 : 1;
    const stage = (label, key, monthly, isCurrency = false) => ({
      label, key, monthly,
      weekly: isCurrency ? monthly / weeks : Math.ceil(monthly / weeks),
      daily: isCurrency ? monthly / wd : Math.ceil(monthly / wd),
      isCurrency,
    });
    return {
      weekdays: wd,
      stages: [
        stage('New Leads', 'leads', leads),
        stage('Discovery Calls', 'discovery', discovery),
        stage('Portals Completed', 'portals', portals),
        stage('Proposals', 'proposals', proposals),
        stage('Lodgements', 'lodgements', lodgements),
        stage('Approvals', 'approvals', approvals),
        stage('Settlements', 'settlements', settlements),
        stage('Settlement Volume', 'settlement_volume', vol, true),
      ],
    };
  }, [volume, conv, month]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        monthly_settlement_volume_target: Number(volume) || 0,
        monthly_submitted_target: Number(lodgeVolume) || 0,
        funnel_avg_loan_size: Number(conv.avg_loan) || 550000,
        funnel_lead_to_discovery: Number(conv.lead_to_discovery) / 100,
        funnel_discovery_to_portal: Number(conv.discovery_to_portal) / 100,
        funnel_portal_to_proposal: Number(conv.portal_to_proposal) / 100,
        funnel_proposal_to_lodgement: Number(conv.proposal_to_lodgement) / 100,
        funnel_lodgement_to_approval: Number(conv.lodgement_to_approval) / 100,
        funnel_approval_to_settlement: Number(conv.approval_to_settlement) / 100,
      };
      const res = await fetch(`${backendUrl}/api/gameplan/targets/${month}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success('Goals saved — full activity plan generated');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not save goals. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetConvDefaults = () => setConv(DEFAULT_CONV);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] p-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="goals-settings-dialog"
      >
        {/* Header */}
        <div className="bg-navy-banner-gradient px-6 py-5 text-white relative overflow-hidden flex-shrink-0">
          <div className="absolute top-2 right-2 w-24 h-24 rounded-full bg-crm-blue/30 blur-2xl opacity-60 pointer-events-none" aria-hidden="true" />
          <DialogHeader className="text-left space-y-1 relative">
            <DialogTitle className="flex items-center gap-2 text-xl font-display font-extrabold text-white">
              <Target className="w-5 h-5 text-crm-blue-light" />
              Set Your <span className="text-crm-blue-light">Monthly Settlement</span> Target
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Enter one number. We&apos;ll auto-derive your full activity funnel — daily, weekly &amp; monthly.
            </DialogDescription>
          </DialogHeader>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-crm-blue" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            {/* Volume input */}
            <div>
              <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                <DollarSign className="w-3.5 h-3.5 text-crm-blue" />
                Monthly Settlement Target
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-slate-400 pointer-events-none">$</span>
                <Input
                  inputMode="numeric"
                  value={volume === '' ? '' : Number(volume).toLocaleString('en-AU')}
                  onChange={(e) => setVolume(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="50,000,000"
                  data-testid="goal-input-volume"
                  className="text-2xl font-extrabold tabular-nums pl-9 h-14"
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setVolume(p)}
                    data-testid={`goal-preset-${p / 1_000_000}M`}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-bold transition-colors border',
                      Number(volume) === p
                        ? 'bg-crm-blue border-crm-blue text-white'
                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-crm-blue hover:text-crm-blue',
                    )}
                  >
                    ${p / 1_000_000}M
                  </button>
                ))}
              </div>
            </div>

            {/* Monthly lodgement $ target — the Command Centre headline. Blank = follow the settlement volume target. */}
            <div>
              <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                <Target className="w-3.5 h-3.5 text-crm-blue" />
                Monthly Lodgement Target
                <span className="ml-1 normal-case tracking-normal font-medium text-slate-400">(headline on the Command Centre · weekly = ¼)</span>
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-slate-400 pointer-events-none">$</span>
                <Input
                  inputMode="numeric"
                  value={lodgeVolume === '' ? '' : Number(lodgeVolume).toLocaleString('en-AU')}
                  onChange={(e) => setLodgeVolume(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={volume ? `${Number(volume).toLocaleString('en-AU')} (same as settlement target)` : '40,000,000'}
                  data-testid="goal-input-lodgement"
                  className="text-lg font-extrabold tabular-nums pl-8 h-12"
                />
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">Leave blank to use the settlement target as the lodgement target.</p>
            </div>

            {/* Live funnel preview */}
            {preview && (
              <div className="bg-slate-50 dark:bg-slate-900/40 border border-surface-border dark:border-slate-800 rounded-xl overflow-hidden" data-testid="funnel-preview">
                <div className="px-4 py-2.5 bg-white dark:bg-slate-900/70 border-b border-surface-border dark:border-slate-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-crm-blue" />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                    Your Auto-Generated Activity Plan
                  </span>
                  <span className="ml-auto text-[11px] text-slate-500 dark:text-slate-400">
                    {preview.weekdays} working days
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white dark:bg-slate-900/40">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-bold">
                      <th className="py-2 px-3 text-left">Stage</th>
                      <th className="py-2 px-3 text-right">Monthly</th>
                      <th className="py-2 px-3 text-right">Weekly</th>
                      <th className="py-2 px-3 text-right">Daily</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {preview.stages.map((s) => {
                      const meta = STAGE_META[s.key] || { icon: Target, color: 'text-slate-500' };
                      const Icon = meta.icon;
                      const fmt = s.isCurrency ? fmtAUD : fmtNum;
                      return (
                        <tr key={s.key} className="hover:bg-white/60 dark:hover:bg-slate-800/40">
                          <td className="py-2 px-3 text-slate-800 dark:text-slate-100">
                            <div className="flex items-center gap-2 font-semibold">
                              <Icon className={cn('w-3.5 h-3.5', meta.color)} />
                              {s.label}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-bold text-slate-900 dark:text-white">{fmt(s.monthly)}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmt(s.weekly)}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmt(s.daily)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Advanced */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 hover:text-crm-blue"
                data-testid="goals-advanced-toggle"
              >
                {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Advanced — funnel assumptions
              </button>
              {showAdvanced && (
                <div className="mt-3 space-y-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-surface-border dark:border-slate-800">
                  <div>
                    <Label className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-1.5 block">
                      Average loan size
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">$</span>
                      <Input
                        inputMode="numeric"
                        value={conv.avg_loan === '' ? '' : Number(conv.avg_loan).toLocaleString('en-AU')}
                        onChange={(e) => setConv({ ...conv, avg_loan: e.target.value.replace(/[^0-9]/g, '') })}
                        className="pl-7 tabular-nums font-semibold"
                        data-testid="goal-avg-loan"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['lead_to_discovery', 'Lead → Discovery'],
                      ['discovery_to_portal', 'Discovery → Portal'],
                      ['portal_to_proposal', 'Portal → Proposal'],
                      ['proposal_to_lodgement', 'Proposal → Lodgement'],
                      ['lodgement_to_approval', 'Lodgement → Approval'],
                      ['approval_to_settlement', 'Approval → Settlement'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1 block uppercase tracking-wide">
                          {label}
                        </Label>
                        <div className="relative">
                          <Input
                            inputMode="numeric"
                            value={conv[key]}
                            onChange={(e) => setConv({ ...conv, [key]: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
                            className="pr-7 tabular-nums font-semibold"
                            data-testid={`conv-${key}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={resetConvDefaults}
                    className="text-xs font-semibold text-crm-blue hover:underline"
                    data-testid="goal-reset-defaults"
                  >
                    Reset to One Broker One Pod defaults
                  </button>
                </div>
              )}
            </div>
            <StageLimitsSection authToken={authToken} isAdmin={isAdmin} />
            <AlertSettingsSection authToken={authToken} isAdmin={isAdmin} />
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-border dark:border-slate-800 flex items-center justify-end gap-2 bg-slate-50 dark:bg-slate-900/50 flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="goals-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || !Number(volume)}
            className="bg-crm-blue hover:bg-navy-banner text-white"
            data-testid="goals-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save Goals
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoalsSettingsDialog;
